import React, { useCallback, useEffect, useState, useContext } from 'react';

// Styles & Assets
import styles from './DevicesManagementPage.module.css';
import { RiFilterFill } from 'react-icons/ri';

// Services
import { dateFormat } from '../../services/parseDates';
import { getFullName } from '../../services/getPatientAtribute';
import { getLastLoan, isDeviceOnLoan } from '../../services/DevicesHelpers';
import { getEnvironmentApiUrl } from '../../services/General';

// Context
import PatientsContext from '../../context/PatientsContext';

// Hooks
import useHttp from '../../hooks/useHttp';
import useSearchbar from '../../hooks/useSearchbar';

// Components
import Table from '../../components/UI/Table/Table';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import Searchbar from '../../components/UI/Searchbar/Searchbar';
import Toolbar from '../../components/UI/Toolbar/Toolbar';
import ButtonAdd from '../../components/UI/ButtonAdd/ButtonAdd';
import Modal from '../../components/UI/Modal/Modal';
import AssignDeviceButton from '../../components/DevicesManagementPage/AssignDeviceButton/AssignDeviceButton';
import DialogAddDevice from '../../components/DevicesManagementPage/DialogAddDevice/DialogAddDevice';
import DeviceStatus from '../../components/DevicesManagementPage/DeviceStatus/DeviceStatus';

// Interface
import { Device, DeviceLoanPatient } from '../../interfaces/Device';
import { Column } from '../../interfaces/Columns';
import { FilterGeneric } from '../../interfaces/Filters';

// Language
import { useTranslation } from 'react-i18next';
import { UserData } from '../../interfaces/UserInfo';
import ButtonToolbar from '../../components/UI/ButtonToolbar/ButtonToolbar';
import DialogFilterDevice from '../../components/DevicesManagementPage/DialogFilterDevice/DialogFilterDevice';
import EnvironmentsString from '../../components/Helpers/EnvironmentsString';

// DevicesManagementPage: page that renders both the Toolbar component and
// the Table component given the COLUMN_DEVICES defined and the devices data fetched.
const DevicesManagementPage: React.FC = () => {
    const { t } = useTranslation();
    const patientsContext = useContext(PatientsContext);

    const { sendRequest: getUser } = useHttp<UserData>();
    const { isLoading, error, sendRequest: getDevices } = useHttp<Device[]>();

    const [devices, setDevices] = useState<Device[]>([]);
    const [showAddDevices, setShowAddDevices] = useState<boolean>(false);
    const [profileIDs, setProfileIDs] = useState<number[]>([]);
    const [isLent, setIsLent] = useState<boolean | null>(null);
    const [selectedDevicesTypes, setSelectedDevicesTypes] = useState<number[]>([]);

    const getZoneName = (id: number | undefined) => {
        const patient = patientsContext.patients.find(patient => patient.id === id);
        if (patient && patient.beds && patient.beds.length > 0) return patient.beds[0].zone?.name || '';
        return '';
    };

    const deleteDeviceLocally = (deviceID: number) => setDevices(oldDevices =>
        oldDevices.filter(oldDevice => oldDevice.id !== deviceID)
    );

    const assignDeviceLocally = (deviceLoan: DeviceLoanPatient) => {
        getUser({
            url: getEnvironmentApiUrl() + 'user/' + deviceLoan.loan_user_id,
        }, (user) => {
            setDevices(oldDevices => {
                const newDevices = [...oldDevices];

                return newDevices.map(oldDevice => {
                    if (oldDevice.id === deviceLoan.device_id) {
                        const patient = patientsContext.patients.find(patient => patient.id === deviceLoan.patient_id);

                        return {
                            ...oldDevice,
                            loans: [{
                                ...deviceLoan,
                                loan_user: user,
                                patient
                            }],
                        };
                    }

                    return oldDevice;
                });
            });
        });
    };

    const widtdrawDeviceLocally = useCallback((deviceID: Device['id']) => {
        setDevices(oldDevices => oldDevices.map(oldDevice => {
            if (oldDevice.id !== deviceID) return oldDevice;
            return { ...oldDevice, loans: [] };
        }));
    }, []);

    const getDeviceProfiles = (device: Device) => {
        return getLastLoan(device.loans)?.patient?.cases?.map(c => c.follow_ups?.map(fw => fw.profile.id) ?? []).flat() ?? [];
    };

    const COLUMNS_DEVICES: Column<Device>[] = [
        {
            id: 0,
            name: () => t('device').slice(0, 4) + '.',
            visible: true,
            sortable: true,
            element: (device) => <DeviceStatus device={device} deleteDeviceCallback={deleteDeviceLocally} />,
            sortElementFunc: (device) => isDeviceOnLoan(device) ? '1' : '-1'
        },
        {
            id: 1,
            name: () => t('device_type'),
            visible: true,
            sortable: true,
            cell: (device) => device.device_type?.description ?? '',
        },
        {
            id: 2,
            name: () => t('manufacturer'),
            visible: true,
            sortable: true,
            cell: (device) => device.model?.manufacturer?.name ?? '',
        },
        {
            id: 3,
            name: () => t('model'),
            visible: true,
            sortable: true,
            cell: (device) => device.model?.name ?? '',
        },
        {
            id: 4,
            name: () => t('loan_professional'),
            visible: true,
            sortable: true,
            cell: (device) => getFullName(getLastLoan(device.loans)?.loan_user) ?? '',
        },
        {
            id: 5,
            name: () => t('loan_date'),
            visible: true,
            sortable: true,
            cell: (device) => dateFormat(getLastLoan(device.loans)?.loan_date),
        },
        {
            id: 6,
            name: () => t('zone'),
            visible: true,
            sortable: true,
            cell: (device) => getZoneName(getLastLoan(device.loans)?.patient_id || undefined),
        },
        {
            id: 7,
            name: () => t('environment'),
            visible: true,
            sortable: true,
            element: (device) => <EnvironmentsString profileIDs={getDeviceProfiles(device)} />,
        },
        {
            id: 8,
            name: () => t('patient'),
            visible: true,
            sortable: true,
            cell: (device) => getFullName(getLastLoan(device.loans)?.patient),
        },
        {
            id: 9,
            name: () => '',
            visible: true,
            sortable: false,
            element: (device) =>
                <AssignDeviceButton
                    device={device}
                    assignDeviceLocally={assignDeviceLocally}
                    widtdrawDeviceLocally={widtdrawDeviceLocally}
                />,
        }
    ];

    const handleConfirmFilters = (selectedProfileIds: number[], lent: boolean | null, selectedDeviceTypes: number[]) => {
        setProfileIDs(selectedProfileIds);
        setIsLent(lent);
        setSelectedDevicesTypes(selectedDeviceTypes);
    };

    const getFilter = useSearchbar(COLUMNS_DEVICES);
    const [filterDevices, setFilterDevices] = useState<FilterGeneric<Device>>(() => getFilter(''));

    const getAllDevices = useCallback(() => {
        getDevices({
            url: getEnvironmentApiUrl() + 'device/all',
            method: 'POST',
            body: { filter: { deleted: false }, params: {} }
        }, (allDevices) => setDevices(allDevices.filter(device => device.device_type?.description !== 'User')));
    }, [getDevices]);

    useEffect(() => {
        getAllDevices();
    }, [getAllDevices]);

    const handleChangeText = (evt: React.ChangeEvent<HTMLInputElement>) =>
        setFilterDevices(() => getFilter(evt.target.value));

    const filteredDevices = devices.filter(device =>
        filterDevices()(device) &&
        (profileIDs.length === 0 || profileIDs.some(pID => getDeviceProfiles(device).includes(pID))) &&
        (selectedDevicesTypes.length === 0 || selectedDevicesTypes.includes(device.device_type_id)) &&
        (
            isLent === null ||
            (isLent && device.loans?.length && device.loans.length > 0) ||
            (isLent === false && (!device.loans || device.loans.length === 0))
        )
    );

    const handleClickAddDevice = () => setShowAddDevices(true);

    return (
        <div className={styles.devicesContainer}>
            <Toolbar text={t('administration')} link='/home/administration'>
                <div className={styles.searchbarContainer}>
                    <Searchbar handleChange={handleChangeText} />
                    <ButtonToolbar
                        icon={<RiFilterFill />}
                        label={t('filter') + ` (${filteredDevices.length}/${devices.length})`}
                    >
                        <DialogFilterDevice
                            defaultProfiles={profileIDs}
                            defaultIsLent={isLent}
                            defaultDeviceType={selectedDevicesTypes}
                            handleConfirm={handleConfirmFilters}
                        />
                    </ButtonToolbar>
                    <ButtonAdd
                        text={t('add_devices')}
                        handleClick={handleClickAddDevice}
                    />
                    <Modal
                        show={showAddDevices}
                        setShowModal={setShowAddDevices}
                    >
                        <DialogAddDevice
                            setShowModal={setShowAddDevices}
                            getAllDevices={getAllDevices}
                        />
                    </Modal>
                </div>
            </Toolbar>

            <ErrorSpinnerWrapper
                error={error}
                loading={isLoading}
            >
                <div className={styles.tableWrapper}>
                    <Table
                        columns={COLUMNS_DEVICES}
                        rows={filteredDevices}
                        nohoverRow
                    />
                </div>
            </ErrorSpinnerWrapper>
        </div>
    );
};

export default DevicesManagementPage;